import { handleAuthFailure, handleAuthResponse } from '../auth';

// Device Library API (zoekedAPI routes/libraryRoutes.js). Every call sends the
// session cookie; errors come back as { error } like the rest of the app.
const base = (groupId) => `${process.env.REACT_APP_API_URL}/library/${groupId}`;

const request = (url, { method = 'GET', body } = {}) =>
    fetch(url, {
        method,
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            ...(body !== undefined ? { 'Content-Type': 'application/json' } : {})
        },
        body: body !== undefined ? JSON.stringify(body) : undefined
    })
        .then(handleAuthResponse)
        .catch(err => { console.log(err); return { error: err.message || 'Network error' }; });

const toQuery = (params) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (Array.isArray(v) ? v.length : v !== undefined && v !== null && v !== '') {
            q.set(k, Array.isArray(v) ? v.join(',') : v);
        }
    });
    const s = q.toString();
    return s ? `?${s}` : '';
};

export const getLibraryConfig = (groupId) => request(`${base(groupId)}/config`);

// params: { type: [], mfr: [], q, status, mine, page, limit, sort }
export const listLibraryItems = (groupId, params) => request(`${base(groupId)}/items${toQuery(params)}`);

export const getLibraryItem = (groupId, itemId) => request(`${base(groupId)}/items/${itemId}`);

export const getItemRevisions = (groupId, itemId) => request(`${base(groupId)}/items/${itemId}/revisions`);

// body: { itemIds, kinds, includeSuperseded } -> { files: [{ itemId, code, kind, fileName, url }], unavailable }
export const requestLibraryDownloads = (groupId, body) =>
    request(`${base(groupId)}/download`, { method: 'POST', body });

// --- Contributor / approver -------------------------------------------------

// A 409 duplicate comes back as { error, existing: { _id, code, revision, status } }
export const createLibraryItem = (groupId, fields) =>
    request(`${base(groupId)}/items`, { method: 'POST', body: fields });

export const updateLibraryItem = (groupId, itemId, fields) =>
    request(`${base(groupId)}/items/${itemId}`, { method: 'PUT', body: fields });

// XHR rather than fetch so the form can show upload progress (0-100)
export const uploadLibraryFile = (groupId, itemId, kind, file, onProgress) => new Promise(resolve => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${base(groupId)}/items/${itemId}/files`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    if (onProgress) {
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded * 100) / e.total)); };
    }
    xhr.onload = () => {
        if (xhr.status === 401) {
            handleAuthFailure();
            resolve({ error: 'Unauthorized' });
            return;
        }
        try {
            resolve(JSON.parse(xhr.responseText) || {});
        } catch (err) {
            resolve({ error: xhr.status === 413 ? 'File is too large' : 'Invalid server response' });
        }
    };
    xhr.onerror = () => resolve({ error: 'Network error' });
    const form = new FormData();
    form.append('kind', kind);
    form.append('file', file);
    xhr.send(form);
});

export const removeLibraryFile = (groupId, itemId, fileId) =>
    request(`${base(groupId)}/items/${itemId}/files/${fileId}`, { method: 'DELETE' });

export const submitLibraryItem = (groupId, itemId, note) =>
    request(`${base(groupId)}/items/${itemId}/submit`, { method: 'POST', body: { note } });

export const approveLibraryItem = (groupId, itemId, { notify, note }) =>
    request(`${base(groupId)}/items/${itemId}/approve`, { method: 'POST', body: { notify, note } });

export const rejectLibraryItem = (groupId, itemId, note) =>
    request(`${base(groupId)}/items/${itemId}/reject`, { method: 'POST', body: { note } });

export const withdrawLibraryItem = (groupId, itemId, note) =>
    request(`${base(groupId)}/items/${itemId}/withdraw`, { method: 'POST', body: { note } });

export const newLibraryRevision = (groupId, itemId) =>
    request(`${base(groupId)}/items/${itemId}/new-revision`, { method: 'POST', body: {} });

export const getReviewQueue = (groupId) => request(`${base(groupId)}/review-queue`);

export const getLibraryRoles = (groupId) => request(`${base(groupId)}/roles`);

export const updateLibraryRoles = (groupId, roles) =>
    request(`${base(groupId)}/roles`, { method: 'PUT', body: roles });
